import { Button as MuiButton } from "@mui/material";
import { Hint } from "@progress/kendo-react-labels/dist/npm/Hint";
import { useAppSelector } from "hooks/redux";
import React from "react";
import { selectAppConfig } from "store/appConfigSlice";
import { ElementOption } from "types/RfiTypes";
import { MAX_LENGTH_OPTION } from "./ElementEditor";

export interface ElementOptionsEditorProps {
  elementId: number;
  options: ElementOption[];
  setOptions: (options: ElementOption[]) => void;
  disabled: boolean;
}

const ElementOptionsEditor = (props: ElementOptionsEditorProps) => {
  // Hooks
  const appConfig = useAppSelector(selectAppConfig);

  // Handlers
  const handleAddOption = () => {
    let newOption: ElementOption = {
      value: undefined,
      description: "",
      sequence: props.options ? props.options.length + 1 : 1,
      type: "default",
    };

    if (props.options && props.options.length > 0) {
      props.setOptions([...props.options, newOption]);
    } else {
      props.setOptions([newOption]);
    }
  };

  const handleRemoveOption = (
    event: React.FormEvent<HTMLButtonElement>,
    index: number
  ) => {
    props.setOptions(
      props.options.filter((opt: ElementOption, i: number) => i !== index)
    );
  };

  const handleUpdateOption = (
    event: React.FormEvent<HTMLInputElement>,
    index: number
  ) => {
    props.setOptions(
      props.options.map((option: ElementOption, oldIndex: number) => {
        if (oldIndex === index) {
          option.description = event.currentTarget.value;
        }
        return option;
      })
    );
  };

  const handleMoveOptionUp = (
    event: React.FormEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (props.options?.length < 2 || props.options[index]?.sequence === 1) {
      console.log(`Error moving option ${index} up`);
      return;
    }

    const options = [...props.options];
    const optionToMove = options[index];
    const optionTarget = options[index - 1];

    if (!optionToMove?.sequence) {
      console.log(`Option target ${index - 1} not found`);
      return;
    }
    if (!optionTarget?.sequence) {
      console.log(`Option to move ${index} not found`);
      return;
    }

    // Move target option "down"
    optionTarget.sequence++;
    options[index] = optionTarget;
    // Move this option "up"
    optionToMove.sequence--;
    options[index - 1] = optionToMove;

    props.setOptions(options);
  };

  const handleMoveOptionDown = (
    event: React.FormEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (
      props.options?.length < 2 ||
      props.options[index]?.sequence === props.options.length
    ) {
      console.log(`Error moving option ${index} down`);
      return;
    }

    const options = [...props.options];
    const optionToMove = options[index];
    const optionTarget = options[index + 1];

    if (!optionToMove?.sequence) {
      console.log(`Error moving option, target ${index + 1} sequence invalid`);
      return;
    }
    if (!optionTarget?.sequence) {
      console.log(
        `Error moving option, option to move ${index} sequence invalid`
      );
      return;
    }

    // Move target option "up"
    optionTarget.sequence--;
    options[index] = optionTarget;
    // Move this option "up"
    optionToMove.sequence++;
    options[index + 1] = optionToMove;

    props.setOptions(options);
  };

  // Render
  return (
    <div className="container my-2">
      <legend className="col-form-label">Options:</legend>
      {props.options.map((option: ElementOption, index: number) => {
        return (
          <div
            className="d-flex flex-row flex-nowrap justify-content-center align-items-center my-1"
            key={`option-${index}`}
          >
            {
              // Don't show Down Arrow on last item
              (
                props.options.length > 0
                  ? option !== props.options[props.options.length - 1]
                  : false
              ) ? (
                <div className="col-1" style={{ textAlign: "center" }}>
                  <button
                    className="text-align-center round-button"
                    aria-label={`Move Option ${option.description} Down`}
                    onClick={(event: React.FormEvent<HTMLButtonElement>) =>
                      handleMoveOptionDown(event, index)
                    }
                    disabled={props.disabled}
                  >
                    <i className="fa-solid fa-arrow-down" />
                  </button>
                </div>
              ) : (
                <div className="col-1"></div>
              )
            }
            {
              // Don't show Up Arrow on first item
              (
                props.options.length > 0 ? option !== props.options[0] : false
              ) ? (
                <div className="col-1" style={{ textAlign: "center" }}>
                  <button
                    className="text-align-center round-button"
                    aria-label={`Move Option ${option.description} Up`}
                    onClick={(event: React.FormEvent<HTMLButtonElement>) =>
                      handleMoveOptionUp(event, index)
                    }
                    disabled={props.disabled}
                  >
                    <i className="fa-solid fa-arrow-up" />
                  </button>
                </div>
              ) : (
                <div className="col-1"></div>
              )
            }
            <input
              className="form-control col"
              type="text"
              value={option?.description ?? ""}
              placeholder={`Enter Option ${index + 1}`}
              maxLength={appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
              aria-label={`Input for Option ${index + 1}`}
              onChange={(event: React.FormEvent<HTMLInputElement>) =>
                handleUpdateOption(event, index)
              }
              disabled={props.disabled}
            />
            <Hint
              direction={"end"}
              className="col-1 align-items-center"
              style={{ minWidth: "50px" }}
            >
              {option?.description?.length ? option.description.length : 0} /{" "}
              {appConfig.maxOptionLength ?? MAX_LENGTH_OPTION}
            </Hint>
            <div className="col-1" style={{ textAlign: "center" }}>
              <button
                className="align-items-center round-button"
                aria-label={`Delete Option ${option.description}`}
                onClick={(event: React.FormEvent<HTMLButtonElement>) =>
                  handleRemoveOption(event, index)
                }
                disabled={props.disabled}
              >
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          </div>
        );
      })}
      <MuiButton
        variant="contained"
        sx={{ marginLeft: "30px" }}
        className="my-2"
        onClick={handleAddOption}
        disabled={props.disabled}
      >
        Add Option
      </MuiButton>
    </div>
  );
};

export default ElementOptionsEditor;
